import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { registerEvent } from '../../services/eventService';

const { Option } = Select;

const resolveEventErrorMessage = (rawMsg, t) => {
  if (!rawMsg) return t('text_submit_registration') + ' failed';
  if (typeof rawMsg === 'string' && (rawMsg.includes('邀请码无效') || rawMsg.includes('不存在') || rawMsg.toLowerCase().includes('invalid invite'))) {
    return t('text_error_invalid_invite_code');
  }
  return t(rawMsg) || rawMsg;
};

const getExpectedPhoneLength = (countryCode) => {
  const clean = String(countryCode || '').replace(/\D/g, '');
  return clean === '852' ? 8 : 11;
};

const getLocalizedFieldTitle = (q, t) => {
  if (!q) return '';
  const title = (q.title || '').trim();
  const type = q.questionType;

  if (title === '姓名' || title.toLowerCase() === 'name') {
    return t('text_shareform_title_name');
  }
  if (
    title === '手机号' ||
    title === '手机号码' ||
    title === '手机' ||
    title === '电话' ||
    title === '電話' ||
    title === '電話號碼' ||
    title === '手機' ||
    title === '手機號' ||
    title.toLowerCase().includes('phone') ||
    type === 'phone'
  ) {
    return t('text_shareform_title_phone');
  }
  if (
    title.includes('发廊') ||
    title.includes('髮廊') ||
    title.toLowerCase().includes('salon')
  ) {
    return t('text_shareform_title_salon');
  }
  if (
    title.includes('邀请码') ||
    title.includes('邀請碼') ||
    title.toLowerCase().includes('invitation') ||
    q.code === 'INVITATION-CODE'
  ) {
    return t('text_shareform_title_code');
  }
  if (
    title === '电邮' ||
    title === '電子郵箱' ||
    title === '电子邮箱' ||
    title === '邮箱' ||
    title === '郵箱' ||
    title.toLowerCase().includes('email') ||
    type === 'email'
  ) {
    return t('text_shareform_title_email');
  }
  return q.title || '';
};

const getLocalizedFieldPlaceholder = (q, t) => {
  if (!q) return '';
  const title = (q.title || '').trim();
  const placeholder = (q.placeholder || '').trim();
  const type = q.questionType;

  if (
    title === '姓名' ||
    title.toLowerCase() === 'name' ||
    placeholder.includes('姓名') ||
    placeholder.toLowerCase().includes('your name')
  ) {
    return t('text_shareform_name_placeholder');
  }
  if (
    title === '手机号' ||
    title === '手机号码' ||
    title === '手机' ||
    title === '电话' ||
    title === '電話' ||
    title === '電話號碼' ||
    title === '手機' ||
    title === '手機號' ||
    title.toLowerCase().includes('phone') ||
    placeholder.includes('手机') ||
    placeholder.includes('手機') ||
    placeholder.includes('电话') ||
    placeholder.includes('電話') ||
    type === 'phone'
  ) {
    return t('text_shareform_phone_placeholder');
  }
  if (
    title.includes('发廊') ||
    title.includes('髮廊') ||
    title.toLowerCase().includes('salon') ||
    placeholder.includes('发廊') ||
    placeholder.includes('髮廊') ||
    placeholder.includes('公司名称') ||
    placeholder.includes('公司名稱')
  ) {
    return t('text_shareform_salon_placeholder');
  }
  if (
    title.includes('邀请码') ||
    title.includes('邀請碼') ||
    title.toLowerCase().includes('invitation') ||
    placeholder.includes('邀请码') ||
    placeholder.includes('邀請碼') ||
    q.code === 'INVITATION-CODE'
  ) {
    return t('text_shareform_code_placeholder');
  }
  if (
    title === '电邮' ||
    title === '電子郵箱' ||
    title === '电子邮箱' ||
    title === '邮箱' ||
    title === '郵箱' ||
    title.toLowerCase().includes('email') ||
    placeholder.includes('邮箱') ||
    placeholder.includes('郵箱') ||
    placeholder.includes('电邮') ||
    placeholder.includes('電郵') ||
    type === 'email'
  ) {
    return t('text_shareform_email_placeholder');
  }
  return q.placeholder || t('text_fill_required');
};

export default function EventRegistrationModal({ open, onClose, event, onSuccess }) {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const user = useSelector((state) => state.authReducer?.user);

  const currentLang = i18n.language === 'en' ? 'en' : (i18n.language === 'zh-HK' ? 'zh-HK' : 'cn');
  const eventData = event?.eventsId || event;
  const localizeObj = eventData?.localize?.[currentLang] || eventData?.localize?.cn || {};
  const eventTitle = localizeObj?.title || eventData?.title || '';
  const layout = localizeObj?.layout || [];

  useEffect(() => {
    if (open && layout.length > 0) {
      const initialValues = {};
      layout.forEach((q) => {
        // Pre-fill phone default country code
        if (q.questionType === 'phone') {
          initialValues[`${q._id}_country`] = q.defaultCountryCode || '+86';
          if (user?.phone) {
            initialValues[q._id] = user.phone;
          }
        } else if (q.questionType === 'email' && user?.email) {
          initialValues[q._id] = user.email;
        } else if (q.title?.includes('姓名') || q.title?.toLowerCase().includes('name')) {
          if (user?.name) {
            initialValues[q._id] = user.name;
          }
        }
      });
      form.setFieldsValue(initialValues);
    } else if (!open) {
      form.resetFields();
    }
  }, [open, layout, user, form]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Build layout array with answers
      const submittedLayout = layout.map((q) => {
        const answerVal = values[q._id] ? String(values[q._id]).trim() : '';

        if (q.questionType === 'phone') {
          const selectedCountry = values[`${q._id}_country`] || q.defaultCountryCode || '+86';
          const updatedCountryCodes = (q.countryCodes || []).map((c) => ({
            ...c,
            isanswer: c.value === selectedCountry || c.label === selectedCountry,
          }));

          return {
            ...q,
            answers: answerVal ? [answerVal] : [],
            countryCodes: updatedCountryCodes,
          };
        }

        return {
          ...q,
          answers: answerVal ? [answerVal] : [],
        };
      });

      const langKey = eventData?.localize?.[currentLang] ? currentLang : 'cn';
      const isValidObjectId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val);
      const eventObjectId =
        (isValidObjectId(eventData?._id) && eventData._id) ||
        (isValidObjectId(event?._id) && event._id) ||
        (isValidObjectId(eventData?.eventsId?._id) && eventData.eventsId._id) ||
        (isValidObjectId(eventData?.eventsId) && eventData.eventsId) ||
        (isValidObjectId(eventData?.eventId) && eventData.eventId);

      if (!eventObjectId) {
        message.error(t('text_shareform_invalid_link') || 'Event is invalid!');
        return;
      }

      const payload = {
        eventId: eventObjectId,
        localize: {
          [langKey]: {
            title: eventTitle || '',
            layout: submittedLayout,
          },
        },
      };

      const res = await registerEvent(payload);
      if (res && (res.status === 200 || res.status === 201 || res.data)) {
        message.success(t('event_text_registration_success'));
        form.resetFields();
        if (onSuccess) {
          onSuccess(res.data);
        }
        onClose();
      } else {
        message.error(resolveEventErrorMessage(res?.message, t));
      }
    } catch (error) {
      console.error('Error submitting event registration:', error);
      const rawMsg = error?.response?.data?.message || error?.message;
      message.error(resolveEventErrorMessage(rawMsg, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      title={
        <div className="text-center pt-2 pb-1">
          <h3 className="text-lg font-bold text-gray-900">{t('text_registration_form')}</h3>
          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{eventTitle}</p>
        </div>
      }
      width={480}
      styles={{
        content: {
          borderRadius: 20,
          padding: '24px 28px 24px',
          boxShadow: '0 20px 45px -10px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        className="mt-4"
      >
        {layout.map((q) => {
          const fieldTitle = getLocalizedFieldTitle(q, t);
          const fieldPlaceholder = getLocalizedFieldPlaceholder(q, t);
          const rules = [];
          if (q.questionType === 'phone') {
            rules.push({
              validator(_, value) {
                const currentCountry = form.getFieldValue(`${q._id}_country`) || q.defaultCountryCode || '+86';
                const expectedLength = getExpectedPhoneLength(currentCountry);
                const cleanDigits = String(value || '').replace(/\D/g, '');
                const cleanCountry = String(currentCountry).replace(/\D/g, '');
                const errMsg = cleanCountry === '852'
                  ? (t('text_invalid_phone_digits_hk') || 'Hong Kong phone number must be 8 digits')
                  : (t('text_invalid_phone_digits_cn') || 'China phone number must be 11 digits');

                if (!cleanDigits) {
                  if (q.isRequired) {
                    return Promise.reject(new Error(errMsg));
                  }
                  return Promise.resolve();
                }

                if (cleanDigits.length !== expectedLength) {
                  return Promise.reject(new Error(errMsg));
                }
                return Promise.resolve();
              },
            });
          } else {
            if (q.isRequired) {
              rules.push({ required: true, message: t('text_fill_required') });
            }
            if (q.questionType === 'email') {
              rules.push({ type: 'email', message: t('Please enter a valid email address') || 'Invalid email format' });
            }
          }

          if (q.questionType === 'phone') {
            const countryCodes = q.countryCodes && q.countryCodes.length > 0
              ? q.countryCodes
              : [
                  { label: '+86', value: '+86', _id: 'cn' },
                  { label: '+852', value: '+852', _id: 'hk' }
                ];

            return (
              <Form.Item
                key={q._id}
                label={
                  <span className="font-medium text-gray-800 text-sm">
                    {fieldTitle} {q.isRequired && <span className="text-red-500">*</span>}
                  </span>
                }
                className="mb-4"
              >
                <div className="flex gap-2 items-start">
                  <Form.Item
                    name={`${q._id}_country`}
                    noStyle
                    initialValue={q.defaultCountryCode || '+86'}
                  >
                    <Select
                      style={{ width: 96 }}
                      size="large"
                      className="!h-10 rounded-lg font-medium [&_.ant-select-selector]:!h-10 [&_.ant-select-selection-item]:!leading-[38px] [&_.ant-select-arrow]:!top-1/2 [&_.ant-select-arrow]:!-translate-y-1/2 [&_.ant-select-arrow]:!m-0"
                      onChange={(val) => {
                        const currentVal = form.getFieldValue(q._id);
                        if (currentVal) {
                          const cleanDigits = String(currentVal).replace(/\D/g, '');
                          const maxLen = getExpectedPhoneLength(val);
                          form.setFieldsValue({ [q._id]: cleanDigits.slice(0, maxLen) });
                        }
                      }}
                    >
                      {countryCodes.map((code) => (
                        <Option key={code._id || code.value} value={code.value}>
                          {code.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name={q._id}
                    rules={rules}
                    validateTrigger={[]}
                    className="flex-1 !mb-0"
                    normalize={(value) => {
                      if (!value) return '';
                      const digits = String(value).replace(/\D/g, '');
                      const currentCountry = form.getFieldValue(`${q._id}_country`) || q.defaultCountryCode || '+86';
                      const maxLen = getExpectedPhoneLength(currentCountry);
                      return digits.slice(0, maxLen);
                    }}
                  >
                    <Input
                      placeholder={fieldPlaceholder}
                      size="large"
                      className="rounded-lg flex-1"
                      onChange={() => {
                        if (form.getFieldError(q._id).length > 0) {
                          form.validateFields([q._id]);
                        }
                      }}
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            );
          }

          return (
            <Form.Item
              key={q._id}
              name={q._id}
              label={
                <span className="font-medium text-gray-800 text-sm">
                  {fieldTitle} {q.isRequired && <span className="text-red-500">*</span>}
                </span>
              }
              rules={rules}
              className="mb-4"
            >
              <Input
                placeholder={fieldPlaceholder}
                size="large"
                className="rounded-lg"
              />
            </Form.Item>
          );
        })}

        <div className="flex gap-3 mt-6">
          <Button
            size="large"
            disabled={submitting}
            onClick={onClose}
            style={{
              flex: 1,
              height: 44,
              borderRadius: 10,
              fontWeight: 600,
            }}
          >
            {t('Cancel')}
          </Button>

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={submitting}
            style={{
              flex: 1,
              height: 44,
              borderRadius: 10,
              fontWeight: 600,
              backgroundColor: '#e50a47',
              borderColor: '#e50a47',
              boxShadow: '0 4px 10px rgba(229, 10, 71, 0.3)',
            }}
          >
            {t('text_submit_registration')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
